<script setup lang="ts">
  defineOptions({
    name: 'ChatMessageEditorInput',
  })

  import { computed, defineAsyncComponent, ref, type PropType } from 'vue'

  import { FaceSmileIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import { useGlobal } from '@common/core-layer/composables/useGlobal'

  import EditorContent from '@common/editor-layer/components/editor/EditorContent.vue'

  import EditorRoot from '@common/editor-layer/components/editor/EditorRoot.vue'

  import { useI18n } from 'vue-i18n'

  import CPButton from '@ui/components/CPButton.vue'
  import CPDrawer from '@ui/components/CPDrawer.vue'
  import type { EditorExpose } from '@common/editor-layer'

  import type { ChatMessageEditorInputExpose, MentionConfig } from '../../types/chat-message-editor.types'

  const EmojiPicker = defineAsyncComponent(() => import('@common/editor-layer/components/editor/tools/EmojiPicker.vue'))

  defineProps({
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isSending: {
      type: Boolean,
      default: false,
    },
    mentionConfig: {
      type: Object as PropType<MentionConfig>,
      default: () => ({
        enableUserMentions: true,
        enableHashMentions: false,
      }),
    },
  })

  const emit = defineEmits<{
    change: []
    submit: []
  }>()

  const { t } = useI18n()

  const editorRootId = createElementId('chat-message-editor', 'input', 'root')

  const editorRef = ref<EditorExpose | null>(null)

  const { isMobileOrTablet } = useGlobal()

  const submitOnEnter = computed(() => !isMobileOrTablet())
  const isOnMobile = computed(() => isMobileOrTablet())

  const showEmojiSheet = ref(false)

  const handleEmojiSelectFromSheet = (emoji: string) => {
    showEmojiSheet.value = false
    const editor = editorRef.value?.getEditor()
    editor?.chain().focus().insertContent(emoji).run()
  }

  const handleEditorChange = () => {
    emit('change')
  }

  const focus = () => {
    editorRef.value?.getEditor()?.commands.focus()
  }

  defineExpose<ChatMessageEditorInputExpose>({
    getText: () => editorRef.value?.getText() || '',
    getHTML: () => editorRef.value?.getHTML() || '',
    setContent: (content: string) => editorRef.value?.setContent(content),
    focus,
  })
</script>

<template>
  <div class="chat-editor-wrapper flex flex-1 items-center gap-2">
    <EditorRoot
      :id="editorRootId"
      ref="editorRef"
      class="flex-1"
      :placeholder="placeholder"
      :editable="!disabled && !isSending"
      :submit-on-enter="submitOnEnter"
      :mention-config="mentionConfig"
      :enable-format-toolbar="true"
      @change="handleEditorChange"
      @submit="emit('submit')"
    >
      <div class="flex items-center gap-2">
        <EditorContent class="chat-editor-content flex-1" />

        <!-- Desktop: inline popover -->
        <!-- tooltip-z-index must exceed ChatContainer z-[10010] so teleported popover appears above it -->
        <EmojiPicker
          v-if="!isOnMobile"
          :disabled="disabled || isSending"
          icon-size="xs"
          placement="top-end"
          :width="320"
          :height="320"
          :teleport-to-body="true"
          :tooltip-z-index="10011"
          :tooltip-text="t('chats.messageEditor.tooltips.emoji')"
        >
          <span class="inline-flex">
            <FaceSmileIcon class="h-4 w-4 cursor-pointer text-secondary hover:text-secondary" />
          </span>
        </EmojiPicker>

        <!-- Mobile: opens bottom sheet -->
        <CPButton
          v-else
          :id="createElementId('chat-editor', 'emoji', 'trigger-mobile')"
          type="text"
          size="sm"
          class="!p-0"
          :disabled="disabled || isSending"
          :aria-label="t('chats.messageEditor.tooltips.emoji')"
          @click="showEmojiSheet = true"
        >
          <FaceSmileIcon class="h-4 w-4 text-secondary" />
        </CPButton>
      </div>
    </EditorRoot>
  </div>

  <!-- Emoji Bottom Sheet (mobile only) -->
  <!-- z-index must exceed ChatContainer z-[10010] -->
  <CPDrawer
    v-if="isOnMobile"
    :id="createElementId('chat-editor', 'mobile', 'emoji-drawer')"
    :show="showEmojiSheet"
    placement="bottom"
    height="80vh"
    :closable="true"
    :show-header="false"
    :z-index="10011"
    body-class="p-0 m-0 flex items-center justify-center"
    @update:show="showEmojiSheet = $event"
  >
    <EmojiPicker variant="inline" @select="handleEmojiSelectFromSheet" />
  </CPDrawer>
</template>

<style scoped>
  .chat-editor-wrapper {
    border-radius: 6px;
    border: 1px solid var(--cp-border-600);
    background: var(--cp-background-500);
    box-shadow: 0 1px 2px 0 var(--cp-shadow);
    padding: 8px 12px;
    min-height: 36px;
    transition:
      border-color 0.2s,
      box-shadow 0.2s;
  }

  .chat-editor-wrapper:focus-within {
    border-color: var(--cp-primary-500);
    box-shadow:
      0 1px 2px 0 var(--cp-shadow),
      0 0 0 2px color-mix(in oklab, var(--cp-primary-500) 10%, transparent);
  }

  .chat-editor-content {
    max-height: 120px;
    min-height: 20px;
    overflow-y: auto;
  }

  .chat-editor-content :deep(.ProseMirror) {
    outline: none;
    font-size: 14px;
    line-height: 1.4;
    font-family: inherit;
  }

  .chat-editor-content :deep(.ProseMirror p) {
    margin: 0;
  }

  .chat-editor-content :deep(.ProseMirror a) {
    color: var(--cp-primary-600);
    font-weight: 500;
    text-decoration: underline;
  }

  .chat-editor-content :deep(.ProseMirror p.is-editor-empty:first-child::before) {
    color: var(--cp-font-300);
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
    font-size: 14px;
  }

  /* iOS Safari auto-zooms any focused input whose computed font-size is < 16px,
     which forces the entire app to scale up, introduces a horizontal scrollbar
     and pushes the sticky chat header above the visible viewport. Force the
     editable surface (and its placeholder) to 16px on touch viewports so Safari
     never triggers the zoom heuristic. Desktop keeps the tighter 14px. */
  @media (max-width: 768px) {
    .chat-editor-content :deep(.ProseMirror),
    .chat-editor-content :deep(.ProseMirror p.is-editor-empty:first-child::before) {
      font-size: 16px;
    }
  }

  .chat-editor-content::-webkit-scrollbar {
    width: 4px;
  }

  .chat-editor-content::-webkit-scrollbar-track {
    background: transparent;
  }

  .chat-editor-content::-webkit-scrollbar-thumb {
    background: var(--cp-font-200);
    border-radius: 2px;
  }
</style>
